import { useCallback } from 'react';
import type { DashboardConfigAndWRMResourceData } from '@atlassian/jira-dashboard-common/src/types.tsx';
import { getUserPreferences } from '@atlassian/jira-dashboard-user-preference/src/services/get-user-preferences/index.tsx';
import { putUserPreferences } from '@atlassian/jira-dashboard-user-preference/src/services/put-user-preferences/index.tsx';
import type { GadgetUserPreferences } from '@atlassian/jira-dashboard-user-preference/src/types.tsx';
import type { RouteResourceUpdater } from '@atlassian/react-resource-router';
import { batch } from '@atlassian/react-sweet-state';
import { lensGadget } from '../utils/dashboard-config-lenses/index.tsx';

type RefreshUserPreferences = (
	arg1: string,
	arg2?: () => void,
) => Promise<GadgetUserPreferences> | null;

export type UserPreferencesService = {
	get: typeof getUserPreferences;
	put: typeof putUserPreferences;
	refresh: RefreshUserPreferences;
};

export const useUserPreferencesService = (
	id: string,
	update: (arg1: RouteResourceUpdater<DashboardConfigAndWRMResourceData>) => void,
): UserPreferencesService => {
	const refresh = useCallback<RefreshUserPreferences>(
		(action, onUpdate) =>
			getUserPreferences(action).then(
				(userPrefs) =>
					new Promise((resolve) => {
						// IMPORTANT
						//  some consumers need batch react state updates with route resource updates
						//  to avoid render race-conditions
						batch(() => {
							update(lensGadget(id, { userPrefs }));
							onUpdate && onUpdate();
							resolve(userPrefs);
						});
					}),
			),
		[id, update],
	);

	return {
		get: getUserPreferences,
		put: putUserPreferences,
		refresh,
	};
};
